import { IconButton } from '@creditornot/cb-button';
import { CaretDown, Cross } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover, PopoverPlacement } from '@creditornot/cb-popover';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Tag } from '@creditornot/cb-tag';
import React, { CSSProperties, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import HierarchyOptions, { Option } from './HierarchyOptions';
import useElementDimension from './useElementDimension';

type Props = {
  caretPosition?: 'left' | 'right';
  className?: string;
  disabled?: boolean;
  invalid?: boolean;
  menuPlacement?: PopoverPlacement;
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  options: Option[];
  placeholder?: string;
  style?: CSSProperties;
  values: string[];
  allowBranchSelection?: boolean;
};

const Root = styled.div`
  div[data-placement='bottom-start'] {
    animation: none;
    transition: none;
  }
`;

const StyledPopover = styled(LegacyPopover)`
  width: 100%;
`;

const SelectControl = styled.div<{ disabled?: boolean; invalid?: boolean; focus?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 2px 12px;
  z-index: 0;
  outline: none;
  border-radius: 8px;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ invalid, focus }) =>
    invalid ? colors.strawberry100 : focus ? colors.wolt100 : cbSemanticCssVars.borderPrimary};
  background-color: ${({ disabled }) =>
    disabled
      ? cssThemedValue({ light: colors.pepper8, dark: colors.salt8 })
      : cssThemedValue({ light: colors.salt100, dark: 'transparent' })};
`;

const Input = styled.input`
  ${typographyCss.Body3()};
  background: transparent;
  outline: none;
  border: none;
  color: ${cbSemanticCssVars.textPrimary};
  margin: 3px 0;
  width: 100%;
`;

const StyledCaretDown = styled(CaretDown)<{ disabled?: boolean }>`
  box-sizing: content-box;
  width: 12px;
  height: 12px;
  padding-inline-start: 8px;
  fill: ${cbSemanticCssVars.textSecondary};
  z-index: 1;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const ClearButton = styled(IconButton).attrs({
  size: 'tiny',
  background: 'animated',
  alignment: 'iconEdges',
  variant: 'grey',
})`
  cursor: pointer;
`;

const LabelTag = styled(Tag).attrs({
  variant: 'lightBlue',
})`
  margin: 3px 4px 3px 0;
  z-index: 1;
`;

const InputContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  overflow: hidden;
  padding-inline-end: 8px;
`;

const InputWrapper = styled.div<{ show?: boolean }>`
  z-index: 1;
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  max-width: ${({ show }) => (show ? '100%' : '0px')};
  min-width: ${({ show }) => (show ? '100px' : '0px')};
`;

const Content = styled(MenuItemGroup)<{ width?: number }>`
  padding: 8px 16px;
  max-height: 332px;
  overflow-y: auto;
  width: ${({ width }) => (width ? `${width}px` : 'auto')};
`;

const MultiLevelMultiSelect = ({
  allowBranchSelection = true,
  caretPosition = 'right',
  menuPlacement = 'bottom-start',
  disabled = false,
  invalid = false,
  placeholder,
  className,
  style,
  options,
  values,
  onChange,
  onBlur,
}: Props) => {
  const { dimension: rootDimension, ref: roofRef } = useElementDimension<HTMLDivElement>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState<string>('');

  const getValueLabel = useCallback((value: string) => options.find(opt => opt.value === value)?.label, [options]);

  const handleSelectOption = useCallback(
    (_value: string | null, newValues: string[]) => {
      onChange(newValues);
    },
    [onChange],
  );

  const handleClickOutside = useCallback(() => {
    setShow(false);
    setSearch('');
    onBlur?.();
  }, [onBlur]);

  const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const search = event.currentTarget.value;
    setSearch(search);
  }, []);

  const handleSelectClick = useCallback(() => {
    if (disabled || show) {
      return;
    }
    inputRef.current?.focus();
    setShow(true);
  }, [disabled, show]);

  const handleRemoveValueClick = useCallback(
    (value: string) => {
      onChange(values.filter(v => v !== value));
    },
    [onChange, values],
  );

  const handleClickInside = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleClearAllClick: React.MouseEventHandler = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setSearch('');
      onChange([]);
    },
    [onChange],
  );

  const handleCaretClick: React.MouseEventHandler = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) {
        return;
      }

      setShow(s => {
        if (s) {
          setSearch('');
          inputRef.current?.blur();
          onBlur?.();
        } else {
          inputRef.current?.focus();
        }

        return !s;
      });
    },
    [disabled, onBlur],
  );

  const handleMouseDown: React.MouseEventHandler = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFocus = useCallback(() => {
    if (disabled) {
      return;
    }

    if (!show) {
      inputRef.current?.focus();
    }
    setShow(true);
  }, [disabled, show]);

  return (
    <Root ref={roofRef} className={className} style={style}>
      <StyledPopover
        show={show}
        placement={menuPlacement}
        renderArrow={false}
        showOnClick={false}
        showOnHover={false}
        disableContentPadding
        onClickOutside={handleClickOutside}
        onClickInside={handleClickInside}
        // Ensure dropdown stay on top of wolt-modal
        contentZIndex={999999}
        content={
          show && (
            <Content width={rootDimension?.width}>
              <HierarchyOptions
                values={values}
                onChange={handleSelectOption}
                search={search}
                options={options}
                caretPosition={caretPosition}
                allowBranchSelection={allowBranchSelection}
              />
            </Content>
          )
        }
      >
        <SelectControl
          onClick={handleSelectClick}
          disabled={disabled}
          invalid={invalid}
          focus={show}
          tabIndex={0}
          onFocus={handleFocus}
        >
          <InputContainer>
            {values.map(value => (
              <LabelTag
                key={value}
                removeDisabled={disabled}
                onMouseDown={handleMouseDown}
                onRemoveClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveValueClick(value);
                }}
              >
                {getValueLabel(value) ?? value}
              </LabelTag>
            ))}

            <InputWrapper show={show || values.length === 0}>
              <Input
                disabled={disabled}
                ref={inputRef}
                value={search}
                placeholder={placeholder}
                onChange={handleSearch}
              />
            </InputWrapper>
          </InputContainer>
          {(values.length !== 0 || search.length !== 0) && (
            <ClearButton disabled={disabled} onMouseDown={handleMouseDown} onClick={handleClearAllClick}>
              <Cross />
            </ClearButton>
          )}
          <StyledCaretDown disabled={disabled} onMouseDown={handleMouseDown} onClick={handleCaretClick} />
        </SelectControl>
      </StyledPopover>
    </Root>
  );
};

export default MultiLevelMultiSelect;
